/* Creating a spot the collection does not hold.
   The panel takes the picker's column over: the same rungs, but free to stand anywhere the
   rules allow rather than only where a chart exists, then where hero's range comes from,
   then the chart itself. A duplicate is the same panel opened on a copy. */

import {
  BRANCH_HELP,
  HERO_ALREADY_ACTED,
  ROLE_OF,
  actionFault,
  actionHelp,
  actionLabel,
  adopt,
  applyNotes,
  blankNotes,
  branchLabel,
  branchesFor,
  contFault,
  fitSeats,
  legalActions,
  legalHeroes,
  legalSeatAt,
  notesOf,
  placeOf,
  presenceOf,
  priorChart,
  priorSources,
  rangeKind,
  rangeSummary,
  seatFault,
  seatHelp,
  seatUnusable,
  seatsFor,
  setHeroRaise,
  sourceKey,
  spotFault,
  spotFromSpec,
  spotKey,
  type RangeKind,
  type Spot,
  seatName,
} from "@preflop/core";
import { el, fill } from "../../ui/dom";
import { BASIC, BASIC_HELP, profileColor } from "../../ui/profiles";
import { group } from "../../ui/tree";
import { brushBar, noteFields } from "./brush";
import { previewNow } from "./chart";
import { flash } from "./flash";
import { dom } from "./layout";
import { dataMessage } from "@preflop/api";
import { takesGtoWizard } from "./paste-gtow";
import { raiseSizeBox, raiseSizeOf, raiseSizeText } from "./raise-size";
import { creatorSpot, outsideFault, previewOf, specOf, trimNotes } from "./preview";
import { saveChart } from "./save";
import {
  CATALOG,
  confirmDiscard,
  jumpTo,
  page,
  profiles,
  render,
  spotLabel,
  suggestions,
  type CreatorDraft,
  type Ladder,
} from "./state";

/** What an entry point names: the rungs it wants the draft to start on. */
type Over = Partial<
  Pick<
    CreatorDraft,
    | "stack"
    | "openSize"
    | "hero"
    | "action"
    | "seats"
    | "branch"
    | "profile"
    | "from"
    | "notes"
    | "touched"
  >
>;

/** Move the draft onto another kind of starting range, picking a sane source. */
function setRangeKind(c: CreatorDraft, kind: RangeKind): void {
  const p = page();
  if (kind === "full" || kind === "typed") {
    c.from = kind;
    return;
  }
  if (kind === "cont") {
    const prior = priorChart(p.spots, c);
    // Nothing to continue from is not a source to guess at: say so instead.
    c.from = prior ? "cont:" + prior.key : "typed";
    return;
  }
  const now = sourceKey(c);
  const keep = now && p.index.has(now) ? now : null;
  c.from = keep ?? (priorChart(p.spots, c) ?? p.spots[0])?.key ?? "full";
}

/** The answer an opponent gave is a rung only some actions have: keep it where it still
    means something, start on the first one where it is new, drop it everywhere else. */
function fitBranch(c: CreatorDraft): void {
  const answers = branchesFor(c.action);
  c.branch = answers ? (answers.find((b) => b === c.branch) ?? answers[0] ?? null) : null;
}

function draftFrom(over: Over): CreatorDraft {
  // Entry points hand in whatever the tree was showing, which need not be a spot.
  const p = page();
  const src = p.spot ?? p.tree;
  const d: CreatorDraft = {
    // A new spot is written for the stakes the tree stands on.
    stakes: src.stakes ?? "NL10-25",
    gameType: src.gameType ?? "Cash Games",
    format: src.format ?? "6Max",
    stack: src.stack ?? "100bb",
    openSize: src.openSize ?? "2.5x",
    hero: src.hero ?? "LJ",
    action: src.action ?? "RFI",
    seats: [...src.seats],
    branch: src.branch ?? null,
    // A new spot is the chart itself unless an entry point asks for a variation.
    profile: null,
    from: "full",
    rangeNote: "",
    rangeFloor: 0,
    touched: false,
    notes: blankNotes(),
    raise: "",
    priority: null,
    typing: { stack: false, openSize: false },
    preview: null,
    ...over,
    moved: [],
  };
  // Settle it the same way a click would, holding whichever dimension the click actually
  // named: from the seat picker the seat is the intent, so the action is what moves.
  if (!legalHeroes(d.gameType, d.action).includes(d.hero)) {
    if (over.action) adopt(d, "action", d.action);
    else adopt(d, "hero", d.hero);
  } else fitSeats(d); // the action decides who the opponents can be
  fitBranch(d);
  // Where hero already acted, the whole grid is the one answer that is always wrong. Start
  // from the chart holding that decision when there is one.
  if (!over.from && HERO_ALREADY_ACTED[d.action])
    setRangeKind(d, priorChart(p.spots, d) ? "cont" : "typed");
  return d;
}

export function openCreator(over: Over = {}): void {
  const p = page();
  if (!p.editMode || !confirmDiscard()) return;
  p.creator = draftFrom(over);
  render();
}

export function duplicateCurrent(): void {
  const p = page();
  if (!p.editMode || !confirmDiscard()) return;
  const spot = p.spot;
  if (!spot) return;
  p.creator = draftFrom({
    seats: [...spot.seats],
    // A duplicate is a copy of what is on screen, profile and all: duplicating a NIT
    // variation starts on NIT, and the key refuses it until something above it moves.
    profile: spot.profile ?? null,
    from: spot.key,
    notes: notesOf(spot),
    touched: true,
  });
  render();
}

/**
 * A variation, written from the grey button on the picker's Opponent row.
 *
 * A variation is by definition a small change to the chart it deviates from, so that chart
 * is where it starts: the creator opens on a copy of the spot's own `Basic Strategy` chart,
 * standing on the profile clicked. Its notation, hero's arriving range and its raise size
 * are all that chart's, drawn the moment the panel opens; the author moves a few hands and
 * saves. Writing an Aggro variation while reading the NIT one still starts from the chart
 * both deviate from.
 *
 * Where the spot has no chart of its own yet, the variation on screen is the source; where
 * nothing at all is charted here, the creator opens as it does from any other grey rung.
 */
export function openVariation(profile: string | null): void {
  const p = page();
  if (!p.editMode || !confirmDiscard()) return;
  const on = p.spot;
  const basic = on ? (p.index.get(spotKey({ ...placeOf(on), profile: undefined })) ?? on) : null;
  openCreator({
    profile,
    ...(on ? { seats: [...on.seats] } : {}),
    ...(basic ? { from: basic.key, notes: notesOf(basic), touched: true } : {}),
  });
}

function closeCreator(): void {
  page().creator = null;
  render();
}

const SOURCE_HELP: Readonly<Record<RangeKind, string>> = {
  full: "Start from all 169 hands",
  cont: "Start from the hands hero still holds after the last chart",
  copy: "Start from another chart's range",
  typed: "Start from a range you write out",
};

const sizeHelp = (key: Ladder, v: string): string =>
  key === "stack" ? `Everyone sits with ${v}` : `The first raise is ${v}`;

export function renderCreator(): void {
  const p = page();
  const host = dom.creator;
  fill(host);
  const c = p.creator;
  host.hidden = !c || !p.editMode;
  if (!c || host.hidden) return;

  // keeps typing out of a full render
  const redraw = (): void => {
    renderCreator();
    previewNow();
  };

  host.append(
    el(
      "header",
      {},
      el("h2", {}, "New spot"),
      el(
        "button",
        {
          type: "button",
          class: "copy",
          title: "Leave without creating the spot",
          onclick: closeCreator,
        },
        "Close",
      ),
    ),
    el("p", { class: "caption" }, [c.stakes, c.gameType, c.format].join(" · ")),
  );

  const rowOf = (...nodes: HTMLElement[]): HTMLElement => el("div", { class: "row" }, nodes);

  /* dimensions with no closed list: the ladder is offered as buttons, and the "+" opens a
     field for a rung nobody thought of. */
  const ladder = (label: string, key: Ladder): HTMLElement => {
    const opts = suggestions(key);
    const list = c[key] && !opts.includes(c[key]) ? [...opts, c[key]] : opts;
    const g = group(
      label,
      list,
      list,
      c[key],
      (v) => {
        c[key] = v;
        c.typing[key] = false;
        redraw();
      },
      {
        dim: key,
        editing: true,
        titleOf: (v) => sizeHelp(key, v),
        onAdd: () => {
          c.typing[key] = true;
          redraw();
        },
      },
    );
    if (c.typing[key]) {
      const inp = el("input", {
        type: "text",
        value: c[key],
        placeholder: key === "stack" ? "35bb" : "3.25x",
      });
      // no redraw: the caret stays put
      inp.addEventListener("input", () => void (c[key] = inp.value));
      inp.addEventListener("keydown", (e) => {
        if (e.key !== "Enter") return;
        c.typing[key] = false;
        redraw();
      });
      g.append(el("div", { class: "meta" }, el("label", {}, `Other ${label.toLowerCase()}`, inp)));
    }
    return g;
  };
  host.append(rowOf(ladder("Stack Size", "stack")), rowOf(ladder("Open Raise", "openSize")));

  /* tree dimensions. Every seat and action the rules forbid is offered disabled, with the
     reason on the tooltip. */
  const pool = seatsFor(c.gameType);
  host.append(
    rowOf(
      group(
        "Hero Seat",
        pool,
        pool.filter((v) => !seatUnusable(c.gameType, v)),
        c.hero,
        (v) => {
          adopt(c, "hero", v);
          fitBranch(c);
          redraw();
        },
        {
          dim: "hero",
          labelOf: seatName,
          titleOf: seatHelp,
          faultOf: (v) => seatUnusable(c.gameType, v),
        },
      ),
    ),
    // The seat is the free choice; the action is read against it. Anything this seat can
    // never be facing is dead, not silently reseated.
    rowOf(
      group(
        "Preflop Action",
        CATALOG.action,
        legalActions(c.gameType, c.hero),
        c.action,
        (v) => {
          adopt(c, "action", v);
          fitBranch(c);
          redraw();
        },
        {
          dim: "action",
          labelOf: actionLabel,
          titleOf: actionHelp,
          faultOf: (v) => actionFault(c.gameType, c.hero, v),
        },
      ),
    ),
  );

  if (c.moved.length)
    host.append(
      el("p", { class: "hint" }, `Moved to keep the spot possible: ${c.moved.join(", ")}.`),
    );

  // The action fixes the opponent count, so there is nothing to add or drop. One rung each,
  // the way the picker draws them: the role names the seat — "Caller Seat", "Squeezer Seat" —
  // so a heading over them saying "Opponent Seats" only says it a second time.
  const roles = ROLE_OF[c.action] ?? [];
  for (const [i, s] of c.seats.entries())
    host.append(
      rowOf(
        group(
          roles[i] ?? `Opponent ${i + 1}`,
          pool,
          legalSeatAt(c.gameType, c.hero, c.action, c.seats, i),
          s,
          (v) => {
            c.seats[i] = v;
            fitSeats(c);
            redraw();
          },
          {
            dim: `seat${i}`,
            labelOf: seatName,
            titleOf: seatHelp,
            faultOf: (v) => seatFault(c.gameType, c.hero, c.action, c.seats, i, v),
          },
        ),
      ),
    );

  // The answer is part of which chart this is, so a new one has to say which it holds: two
  // drafts that differ only there are two spots.
  const answers = branchesFor(c.action);
  if (answers)
    host.append(
      rowOf(
        group(
          branchLabel(c.seats.map((s) => s ?? "")),
          answers,
          answers,
          c.branch,
          (v) => {
            c.branch = v;
            redraw();
          },
          { dim: "branch", titleOf: (v) => BRANCH_HELP[v] ?? v },
        ),
      ),
    );

  /* Which opponent the chart is written against. The same buttons as the picker's row, under
     the same caption — two names for one row would read as two different rows — and changing
     one changes the rung and nothing else: the range source stays what it was, as the source
     row below goes on saying. */
  const list = profiles();
  host.append(
    rowOf(
      group(
        "Variations",
        [BASIC, ...list.map((o) => o.id)],
        [BASIC, ...list.map((o) => o.id)],
        c.profile ?? BASIC,
        (v) => {
          c.profile = v === BASIC ? null : v;
          redraw();
        },
        {
          dim: "profile",
          keyOf: (v) => v,
          labelOf: (v) => (v === BASIC ? v : (list.find((o) => o.id === v)?.name ?? v)),
          dotOf: (v) => (v === BASIC ? "" : profileColor(list, v)),
          titleOf: (v) => (v === BASIC ? BASIC_HELP : (list.find((o) => o.id === v)?.note ?? "")),
        },
      ),
    ),
  );

  /* where hero's range comes from */
  // Changing the stack, size or seats can strand a source that no longer matches. Settle
  // that before drawing, so the panel never shows a chart the rules above would refuse.
  const contBlocked = contFault(p.spots, c);
  if (rangeKind(c) === "cont" && !priorSources(p.spots, c).some((sp) => sp.key === sourceKey(c)))
    setRangeKind(c, contBlocked ? "typed" : "cont");

  const kind = rangeKind(c);
  // Name the action that carries hero forward rather than "continued with": into a 4-bet
  // spot only hero's 3-bets arrive, and the button should say so.
  const step = HERO_ALREADY_ACTED[c.action];
  const KINDS: readonly (readonly [RangeKind, string])[] = [
    ["full", "Full grid (169 hands)"],
    ["cont", step ? `Hands hero ${step.did}…` : "Hands hero continued with…"],
    ["copy", "Copy from…"],
    ["typed", "Type the range"],
  ];
  const srcCol = el(
    "div",
    { class: "group" },
    el("span", {}, "Starting Range"),
    el(
      "div",
      { class: "btns" },
      KINDS.map(([k, text]) => {
        const fault = k === "cont" ? contBlocked : "";
        return el(
          "button",
          {
            type: "button",
            class: "btn" + (kind === k ? " on" : ""),
            disabled: !!fault,
            title: fault || SOURCE_HELP[k],
            onclick: () => {
              setRangeKind(c, k);
              redraw();
            },
          },
          text,
        );
      }),
    ),
  );

  // What the choice above actually produces, so it can be checked before any frequency is
  // written into it.
  const rangeNote = el("p", { class: "hint" }, rangeSummary(p.index, c));

  if (kind === "copy" || kind === "cont") {
    const cur = sourceKey(c);
    // Continuing from a chart is only meaningful within one game; copying is a template, so
    // it may come from anywhere.
    const from: readonly Spot[] = kind === "cont" ? priorSources(p.spots, c) : p.spots;
    const sel = el(
      "select",
      {},
      from
        .map((sp) => ({ sp, label: spotLabel(sp) }))
        .sort((a, b) => a.label.localeCompare(b.label))
        .map(({ sp, label }) => el("option", { value: sp.key, selected: sp.key === cur }, label)),
    );
    sel.onchange = () => {
      c.from = kind === "cont" ? "cont:" + sel.value : sel.value;
      const picked = p.index.get(sel.value);
      if (!c.touched && kind === "copy" && picked) c.notes = notesOf(picked);
      redraw();
    };
    const wrap = el(
      "div",
      { class: "meta" },
      el("label", {}, kind === "cont" ? "Hero's previous decision" : "Source chart", sel),
    );

    if (kind === "cont") {
      // Hands that arrive at a trickle are noise in most charts; the floor drops them
      // rather than carrying a 1% sliver through every total.
      const fin = el("input", {
        type: "number",
        min: "0",
        max: "99",
        step: "1",
        value: String(Math.round(c.rangeFloor * 100)),
      });
      fin.addEventListener("input", () => {
        c.rangeFloor = Math.max(0, Math.min(99, parseFloat(fin.value) || 0)) / 100;
        rangeNote.textContent = rangeSummary(p.index, c); // no redraw: caret stays
        previewNow();
      });
      wrap.append(
        el("label", {}, `Drop hands hero ${step ? step.does : "continues"} under (%)`, fin),
      );
    }
    srcCol.append(wrap);
  }

  if (kind === "typed") {
    const ta = el("textarea", {
      rows: 2,
      value: c.rangeNote,
      spellcheck: false,
      placeholder: "AA,KK,QQ,AKs,[50]JJ,ATs[/50]",
    });
    ta.addEventListener("input", () => {
      c.rangeNote = ta.value;
      rangeNote.textContent = rangeSummary(p.index, c);
      previewNow();
    });
    takesGtoWizard(ta);
    srcCol.append(el("div", { class: "meta" }, el("label", {}, "Hero's range", ta)));
  }

  srcCol.append(rangeNote);
  host.append(rowOf(srcCol));

  /* the range itself */
  host.append(brushBar());
  // The size box reads the spot the draft describes so far. Until it is typed into it shows
  // the table's price, which follows the seats, the open and the answer as they are picked.
  const table = {
    stack: c.stack,
    openSize: c.openSize,
    hero: c.hero,
    action: c.action,
    seats: c.seats.filter((s): s is string => !!s),
    ...(c.branch ? { branch: c.branch } : {}),
  };
  noteFields(
    host,
    c.notes,
    (a) => {
      c.touched = true;
      c.priority = a;
      previewNow();
    },
    {
      under: {
        raise: raiseSizeBox(table, c.raise || raiseSizeText(table), (text) => (c.raise = text)),
      },
      // A solver range pasted into the chart is read against the hands hero gets here with,
      // which is the range the panel above just chose.
      arriving: () => {
        try {
          const sp = creatorSpot(c);
          return sp ? presenceOf(sp) : null;
        } catch {
          return null; // half a draft describes no range yet: nothing to read against
        }
      },
    },
  );

  host.append(
    el(
      "p",
      { class: "hint" },
      "Enter only what hero does: fold is the remainder, and it is measured against hero's " +
        "range above, not against all 169 hands. Leave all three empty for a spot hero folds " +
        "outright — that is a chart like any other.",
    ),
  );

  const msg = el("p", { class: "msg err", hidden: true });
  const fail = (text: string, extra?: HTMLElement): void => {
    fill(msg, text, extra ? " " : null, extra);
    msg.hidden = false;
  };

  const create = el(
    "button",
    { type: "button", class: "copy primary", title: "Add this chart to the collection" },
    "Create spot",
  );
  const cancel = el(
    "button",
    {
      type: "button",
      class: "copy",
      title: "Leave without creating the spot",
      onclick: closeCreator,
    },
    "Cancel",
  );
  host.append(msg, el("div", { class: "actions" }, create, cancel));

  create.onclick = async () => {
    if (!c.stack.trim() || !c.openSize.trim() || !c.hero || !c.action)
      return fail("Stack size, open raise, hero seat and action are all required.");
    if (c.seats.some((s) => !s)) return fail("Pick every opponent seat.");
    const fault = spotFault(c.gameType, c.hero, c.action, c.seats);
    if (fault) return fail(fault);
    const spec = specOf(c);
    if (!spec) return fail("Stack size, open raise, hero seat and action are all required.");

    // Nothing written is a chart too: hero folds every hand it arrives with. The matrix
    // above has been drawing that chart all along.
    const notes = trimNotes(c.notes);

    // The key is the one thing that makes a spot unique: one place in the tree, one chart.
    // No silent overwrite.
    const key = spotKey(spec);
    const existing = p.index.get(key);
    if (existing)
      return fail(
        "This spot already exists.",
        el(
          "button",
          {
            type: "button",
            class: "copy",
            title: "Go to the chart that already holds this spot",
            onclick: () => {
              jumpTo(existing);
              p.creator = null;
              render();
            },
          },
          "Open it",
        ),
      );

    // Built and validated in full before any of it is kept: a typo leaves nothing behind.
    let warn: string[];
    try {
      const sp = spotFromSpec(p.index, spec);
      if (!sp) return fail("The source chart is no longer available.");
      // The same refusal the editor greys Save for: a chart naming a hand hero does not hold
      // here is a chart of somewhere else. The creator has no live line to turn red, so the
      // refusal lands on the click, where its other refusals already land.
      const outside = previewOf(sp, notes, c.priority).outside;
      if (outside.length) return fail(outsideFault(outside));
      warn = applyNotes(sp, notes, c.priority);
      setHeroRaise(sp, raiseSizeOf(c.raise));
      // Anything but "saved" leaves the creator as it is: see saveChart.
      if ((await saveChart(sp)) !== "saved") return;
    } catch (e) {
      return fail(dataMessage(e));
    }

    const made = p.index.get(key);
    if (made) jumpTo(made);
    p.creator = null;
    p.dirty = false;
    render();
    // Said every time: the chart went over the network, and the user should know it arrived.
    flash([`Saved into "${p.session.collection.name}".`, ...warn].join(" · "), "info");
  };
}
